import { rd, RemoteData } from '@passionware/monads';
import { useMemo } from 'react';
import type { Selection } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import type { ResourceMembership } from 'v5/core/entity-schema/entitySchema.types';
import { Button } from 'v5/design-system/Button';
import { Label } from 'v5/design-system/Label';
import {
  ListBox,
  ListBoxHeader,
  ListBoxItem,
  ListBoxItemContent,
  ListBoxSkeleton
} from 'v5/design-system/ListBox';
import { RadioGroup, RadioGroupItem } from 'v5/design-system/RadioGroup';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { renderError } from 'v5/patterns/widgets/renderError';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';

export interface WorkspaceMembershipPickerProps {
  value: ResourceMembership;
  onChange(value: ResourceMembership): void;
  workspaces: RemoteData<EnterpriseWorkspace[]>;
  /** Field ids must be unique per picker instance. */
  idPrefix: string;
}

/**
 * Hidden / Global / Specific-workspaces picker producing a
 * `ResourceMembership`. Extracted from `AssignResourcesToWorkspacesDialog`
 * so the create form can choose membership before the first `POST`.
 */
export function WorkspaceMembershipPicker({
  value,
  onChange,
  workspaces,
  idPrefix
}: WorkspaceMembershipPickerProps) {
  const { t } = useTranslation('resources', {
    keyPrefix: 'AssignResourcesToWorkspacesDialog'
  });
  const selectedIds = value.type === 'workspaces' ? value.workspaceIds : [];
  const selectedKeys = useMemo(
    () => new Set(selectedIds.map(id => String(id))),
    [selectedIds]
  );
  const loaded = rd.tryGet(workspaces) ?? [];

  const setSelectedIds = (ids: number[]) =>
    onChange({ type: 'workspaces', workspaceIds: ids });

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      setSelectedIds(loaded.map(workspace => Number(workspace.id)));
      return;
    }
    setSelectedIds(
      Array.from(keys)
        .map(key => Number(key))
        .filter(id => Number.isFinite(id))
    );
  };

  return (
    <div className="space-y-4" data-testid="workspace-membership-picker">
      <div className="space-y-2">
        <p className="text-base font-medium text-fg-default">
          {t('fieldScope')}
        </p>
        <RadioGroup
          value={value.type}
          onValueChange={next => {
            if (next === 'hidden') onChange({ type: 'hidden' });
            if (next === 'global') onChange({ type: 'global' });
            if (next === 'workspaces') setSelectedIds(selectedIds);
          }}
          className="gap-3"
        >
          <div className="flex items-start gap-2.5">
            <RadioGroupItem value="hidden" id={`${idPrefix}-hidden`} />
            <div className="min-w-0 space-y-0.5">
              <Label htmlFor={`${idPrefix}-hidden`} className="cursor-pointer">
                {t('scopeHidden')}
              </Label>
              <p className="text-sm text-fg-muted">{t('scopeHiddenHint')}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <RadioGroupItem value="global" id={`${idPrefix}-global`} />
            <div className="min-w-0 space-y-0.5">
              <Label htmlFor={`${idPrefix}-global`} className="cursor-pointer">
                {t('scopeGlobal')}
              </Label>
              <p className="text-sm text-fg-muted">{t('scopeGlobalHint')}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <RadioGroupItem value="workspaces" id={`${idPrefix}-workspaces`} />
            <Label
              htmlFor={`${idPrefix}-workspaces`}
              className="cursor-pointer"
            >
              {t('scopeWorkspaces')}
            </Label>
          </div>
        </RadioGroup>
      </div>

      {value.type === 'workspaces' && (
        <div className="space-y-2">
          <p className="text-base font-medium text-fg-default">
            {t('fieldWorkspaces')}
          </p>
          <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border-default">
            <ListBoxHeader>
              <span className="text-sm text-fg-muted">
                {selectedIds.length > 0
                  ? t('workspacesSelectedCount', { count: selectedIds.length })
                  : t('workspacesSelectPrompt')}
              </span>
              {selectedIds.length > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="-my-2"
                  onClick={() => setSelectedIds([])}
                >
                  {t('workspacesUnselectAll')}
                </Button>
              ) : null}
            </ListBoxHeader>
            <ScrollShadowWrapper className="max-h-72 min-h-0 overflow-y-auto">
              {rd
                .journey(workspaces)
                .wait(<ListBoxSkeleton numRows={4} showExtra={false} />)
                .catch(renderError)
                .map(loadedWorkspaces =>
                  loadedWorkspaces.length === 0 ? (
                    <div className="p-6 text-center text-sm text-fg-muted">
                      {t('workspacesEmpty')}
                    </div>
                  ) : (
                    <ListBox
                      aria-label={t('fieldWorkspaces')}
                      selectionMode="multiple"
                      selectedKeys={selectedKeys}
                      onSelectionChange={handleSelectionChange}
                    >
                      {loadedWorkspaces.map(workspace => {
                        const workspaceId = Number(workspace.id);
                        return (
                          <ListBoxItem
                            key={workspaceId}
                            id={String(workspaceId)}
                            textValue={workspace.name}
                          >
                            <ListBoxItemContent
                              icon={
                                <EnterpriseWorkspaceIcon
                                  name={workspace.name}
                                  iconUrl={workspace.iconUrl}
                                />
                              }
                              primary={workspace.name}
                            />
                          </ListBoxItem>
                        );
                      })}
                    </ListBox>
                  )
                )}
            </ScrollShadowWrapper>
          </div>
        </div>
      )}
    </div>
  );
}
